import React, { useImperativeHandle, useRef, useState, useEffect } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import Input from '@/component/input/Input.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useMutationUpdateSettings, useMutationUpdateDeviceSerial } from '@/api/settings/settings.ts'
import { apiChangePassword } from '@/api/auth/changePassword.ts'
import { useLoginStore } from '@/store/LoginStore.ts'
import { equalString, isNotEmpty } from '@/util/strings.ts'
import DaumPostcode from 'react-daum-postcode'
import axios from 'axios'
import { GOOGLE_MAPS_API_KEY } from '@/constant/constant.ts'

export interface IProps {
    title: string
    settingKey: string
    type: 'text' | 'password' | 'address'
    placeholder: string
    currentAddress?: string // 현재 주소 (address 타입에서 사용)
    currentValue?: string
    onSettingChange?: (key: string, newValue: string) => void
    onPasswordChange?: () => void
    onAddressChange?: (address: string, latitude: string, longitude: string) => void
}

const ChangeSettingsModal = ({ title, settingKey, type, placeholder, currentAddress, currentValue, onSettingChange, onPasswordChange, onAddressChange }: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const { t } = useTranslation()
    const { logout } = useLoginStore()

    const { mutate: updateSetting } = useMutationUpdateSettings()
    const { mutate: updateDeviceSerial } = useMutationUpdateDeviceSerial()

    useImperativeHandle(ref, () => ({
        show: () => {
            // 새로운 세션 시작 시 입력값 초기화 (패스워드 모드일 경우)
            if (type === 'password') {
                setCurrentPassword('')
                setNewPassword('')
                setNewPasswordRe('')
            } else if (type === 'text') {
                // 기존 현재값 유지 또는 초기화
                if (currentValue) {
                    setNewValue(currentValue)
                }
            }
            
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    const [newValue, setNewValue] = useState<string>('')
    const [currentPassword, setCurrentPassword] = useState<string>('')
    const [newPassword, setNewPassword] = useState<string>('')
    const [newPasswordRe, setNewPasswordRe] = useState<string>('')

    // 주소 관련 상태
    const [isPostcodeOpen, setIsPostcodeOpen] = useState(false)
    const [basicAddress, setBasicAddress] = useState('')
    const [detailedAddress, setDetailedAddress] = useState('')
    const [latitude, setLatitude] = useState('')
    const [longitude, setLongitude] = useState('')
    const [isAddressEditMode, setIsAddressEditMode] = useState(false)

    // 모달이 열릴 때 현재 주소값 설정
    useEffect(() => {
        if (type === 'address' && currentAddress) {
            setBasicAddress(currentAddress)
        }
        if (type === 'text' && currentValue) {
            setNewValue(currentValue)
        }
    }, [type, currentAddress, currentValue])

    // Google Maps Geocoding API를 호출하여 위도/경도 가져오기
    const fetchCoordinates = async (address: string) => {
        const API_KEY = GOOGLE_MAPS_API_KEY
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${API_KEY}`
        try {
            const response = await axios.get(url)
            const location = response.data.results[0]?.geometry?.location
            if (location) {
                setLatitude(location.lat.toString())
                setLongitude(location.lng.toString())
                return { latitude: location.lat.toString(), longitude: location.lng.toString() }
            }
            return null
        } catch (error) {
            console.error('Geocoding API 호출 실패:', error)
            toast.error(t('주소를 기반으로 좌표를 가져오지 못했습니다.'))
            return null
        }
    }

    // 우편번호 검색 결과 처리
    const handleComplete = (data: any) => {
        let fullAddress = data.roadAddress || data.jibunAddress
        let extraAddress = ''

        if (data.bname && /[동|로|가]$/g.test(data.bname)) {
            extraAddress += data.bname
        }
        if (data.buildingName && data.apartment === 'Y') {
            extraAddress += (extraAddress ? `, ${data.buildingName}` : data.buildingName)
        }

        if (extraAddress) {
            fullAddress += ` (${extraAddress})`
        }

        setBasicAddress(fullAddress)
        setDetailedAddress('')
        setIsPostcodeOpen(false)
        setIsAddressEditMode(true)

        // Google Maps Geocoding API 호출
        fetchCoordinates(fullAddress)
    }

    // 상세주소 입력 핸들러
    const handleDetailedAddressChange = (value: string) => {
        setDetailedAddress(value)
    }

    const onClickSubmit = async () => {
        if (type === 'password') {
            if (isNotEmpty(newPassword) && !equalString(newPassword, newPasswordRe)) {
                toast.error(t('changePasswordModal.submit.error.passwordNotSame'))
                return
            }

            const { code } = await apiChangePassword({ currentPassword, newPassword })
            if (code === 200) {
                toast.success(t('changePasswordModal.submit.success'))
                modalRef.current?.close()
                await logout()
                sessionStorage.clear()
                localStorage.clear()
                onPasswordChange?.()
                window.location.href = '/login'
            } else {
                toast.error(t('changePasswordModal.submit.error.failToChangePassword'))
            }
        } else if (type === 'address') {
            const fullAddress = detailedAddress ? `${basicAddress} ${detailedAddress}`.trim() : basicAddress

            if (!fullAddress) {
                toast.error(t('changeSettingsModal.error.empty'))
                return
            }

            // 좌표가 없는 경우 주소로부터 가져오기 시도
            let coords
            if (!latitude || !longitude) {
                coords = await fetchCoordinates(fullAddress)
                if (!coords) {
                    toast.error(t('주소를 기반으로 좌표를 가져오지 못했습니다.'))
                    return
                }
            } else {
                coords = { latitude, longitude }
            }

            // 주소와 좌표 업데이트
            updateSetting(
                { 
                    address: fullAddress,
                    latitude: parseFloat(coords.latitude),
                    longitude: parseFloat(coords.longitude)
                },
                {
                    onSuccess: () => {
                        toast.success(t('changeSettingsModal.success'))
                        modalRef.current?.close()
                        onAddressChange?.(fullAddress, coords.latitude, coords.longitude)
                        setIsAddressEditMode(false)
                    },
                    onError: (error) => {
                        console.error("안심구역 설정 변경 실패:", error)
                        toast.error(t('changeSettingsModal.error.updateFail'))
                    },
                }
            )
        } else {
            if (!newValue.trim()) {
                toast.error(t('changeSettingsModal.error.empty'))
                return
            }

            if (settingKey === 'deviceSerial') {
                updateDeviceSerial(newValue, {
                    onSuccess: () => {
                        toast.success(t('changeSettingsModal.success'))
                        modalRef.current?.close()
                        onSettingChange?.(settingKey, newValue)
                    },
                    onError: (error: any) => {
                        console.error(" 디바이스 시리얼 변경 실패:", error)
                        if (error?.response?.data?.code === 400 && error?.response?.data?.message === "이미 등록된 시리얼입니다.") {
                            toast.error(t('changeSettingsModal.error.duplicateSerial'))
                        } else {
                            toast.error(t('changeSettingsModal.error.updateFail'))
                        }
                    },
                })
            } else {
                updateSetting(
                    { [settingKey]: newValue },
                    {
                        onSuccess: () => {
                            toast.success(t('changeSettingsModal.success'))
                            modalRef.current?.close()
                            onSettingChange?.(settingKey, newValue)
                        },
                        onError: (error) => {
                            console.error(" 설정 변경 실패:", error)
                            toast.error(t('changeSettingsModal.error.updateFail'))
                        },
                    }
                )
            }
        }
    }

    return (
        <BaseModal ref={modalRef} showCloseButton={false} width={type === 'address' ? '400' : '400'}>
            <div className={'flex items-center justify-center px-[30px] py-[40px]'}>
                <div className={'w-full grid gap-[10px] max-w-[400px]'}>
                    <p className={'text-center font-bold'}>{title}</p>

                    {type === 'password' ? (
                        <>
                            <Input
                                value={currentPassword}
                                placeholder={t('changePasswordModal.current.placeholder')}
                                inputType={'password'}
                                onChanged={text => setCurrentPassword(text)}
                            />
                            <Input
                                value={newPassword}
                                placeholder={t('changePasswordModal.new.placeholder')}
                                inputType={'password'}
                                onChanged={text => setNewPassword(text)}
                            />
                            <Input
                                value={newPasswordRe}
                                placeholder={t('changePasswordModal.newRe.placeholder')}
                                inputType={'password'}
                                onChanged={text => setNewPasswordRe(text)}
                            />
                        </>
                    ) : type === 'address' ? (
                        <div className="flex flex-col gap-3">
                            {!isAddressEditMode ? (
                                <div className="flex justify-between items-center">
                                    <div className="overflow-hidden text-ellipsis">
                                        <p className="text-sm text-gray-500">{basicAddress || currentAddress || t('주소 정보 없음')}</p>
                                    </div>
                                    <button 
                                        onClick={() => setIsPostcodeOpen(true)}
                                        className="bg-gray-200 px-3 py-1 rounded-md text-sm"
                                    >
                                        {t('주소검색')}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex gap-2">
                                        <Input
                                            className="w-full"
                                            inputType="text"
                                            placeholder={t('주소')}
                                            value={basicAddress}
                                            disabled
                                        />
                                        <button
                                            onClick={() => setIsPostcodeOpen(true)}
                                            className="bg-gray-200 px-4 py-2 rounded-md"
                                        >
                                            {t('주소검색')}
                                        </button>
                                    </div>
                                    <Input
                                        className="w-full"
                                        inputType="text"
                                        placeholder={t('상세주소')}
                                        value={detailedAddress}
                                        onChanged={handleDetailedAddressChange}
                                    />
                                </>
                            )}
                        </div>
                    ) : (
                        <Input
                            value={newValue}
                            placeholder={placeholder}
                            inputType={'text'}
                            onChanged={text => setNewValue(text)}
                        />
                    )}

                    {isPostcodeOpen && (
                        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-gray-800 bg-opacity-50 z-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg relative max-w-[500px] w-full">
                                <button
                                    className="absolute top-2 right-2 text-gray-500"
                                    onClick={() => setIsPostcodeOpen(false)}
                                >
                                    ✖
                                </button>
                                <DaumPostcode onComplete={handleComplete} />
                            </div>
                        </div>
                    )}

                    <div className={'flex gap-[10px] mt-[20px]'}>
                        <BorderButton text={t('changeSettingsModal.cancel')} className={'flex-1'}
                                      onClick={() => {
                                          if (type === 'address') {
                                              setIsAddressEditMode(false)
                                          }
                                          modalRef?.current?.close()
                                      }} />
                        <SolidButton text={t('changeSettingsModal.confirm')} className={'flex-1'} onClick={onClickSubmit} />
                    </div>
                </div>
            </div>
        </BaseModal>
    )
}

export default React.forwardRef(ChangeSettingsModal)
